import { FC, useEffect } from "react";
import { useUserReport } from "src/features/reporting/hooks";
import { useFormContext, useWatch } from "react-hook-form";
import UserDataTable from "../../../components/DataTable/UserDataTable";
import dayjs from "dayjs";

interface Props {
	formId: number;
	year: number;
	storeId?: number;
	areaId?: number;
	compliancy?: boolean;
	onFormSelect?: (id: number, month: string, observationform: number) => void;
	observationType?: IObservationType;
	calendarMonth?: Month;
	calendaryYear?: number;
	onDownloadReady?: (downloadFn: () => void) => void;
}

const MonthlyUserReport: FC<Props> = ({
	formId,
	year,
	storeId,
	areaId,
	compliancy,
	onFormSelect,
	observationType,
	calendarMonth,
	calendaryYear,
	onDownloadReady
}) => {
	const { control } = useFormContext<ReportDetailForm>();

	const { showUngradedEmployees } = useWatch({
		control,
		name: "filters"
	});

	const { columns, data, isSuccess, isLoading, download } = useUserReport(
		year,
		formId,
		storeId || 0,
		areaId,
		compliancy,
		observationType
	);

	const employeesInStore = data?.filter(
		(userReport: UserReportTable) =>
			userReport.great_conversation_store_id === storeId
	);

	const ungradedEmployees = employeesInStore?.filter(
		(userReport: UserReportTable) => {
			const currentMonth = dayjs().format("MMM");
			return (
				Boolean(userReport[currentMonth]) &&
				!userReport[currentMonth].graded &&
				userReport[currentMonth].complete
			);
		}
	);

	const employees =
		observationType === "GREAT_CONVERSATION" ? employeesInStore : data;

	const userReports = showUngradedEmployees ? ungradedEmployees : employees;

	useEffect(() => {
		if (download && onDownloadReady) {
			onDownloadReady(download);
		}
	}, [download, onDownloadReady]);

	return (
		<>
			{isLoading && (
				<div className="flex justify-center items-center py-12">
					<div className="w-5 h-5 border-2 border-border border-t-primary rounded-full animate-spin" />
				</div>
			)}
			{isSuccess && userReports && (
				<UserDataTable
					columns={columns}
					data={userReports}
					onFormSelect={onFormSelect}
					calendarMonth={calendarMonth}
					observationType={observationType}
					calendarYear={calendaryYear}
					emptyDataMessage={
						showUngradedEmployees
							? "No ungraded employees found"
							: "No user data found"
					}
				/>
			)}
		</>
	);
};

export default MonthlyUserReport;
